import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

/* ============================================================
 * FENISA VARIANT GALLERY - QUICK ADD
 * ALT örnekleri:
 *   [vg:Gold] ...
 *   [vg:Light Pink] ...
 *   [vg:Bronze Glow] ...
 *   [vg:all] ...
 * ============================================================ */

const fenisaNormalizeVG = (value) =>
  String(value || '')
    .trim()
    .toLocaleLowerCase('tr-TR')
    .replace(/\s+/g, ' ');

const fenisaIsColorOptionName = (name) => {
  const normalized = fenisaNormalizeVG(name);

  return (
    normalized === 'renk' ||
    normalized === 'color' ||
    normalized === 'colour'
  );
};

const fenisaGetSelectedColor = (root) => {
  const picker = root?.querySelector?.('variant-picker');
  if (!picker) return '';

  const checkedInputs = picker.querySelectorAll(
    'input[data-option-name]:checked'
  );

  for (const input of checkedInputs) {
    if (fenisaIsColorOptionName(input.dataset.optionName)) {
      return input.value || '';
    }
  }

  const selects = picker.querySelectorAll('select');

  for (const select of selects) {
    const selectedOption = select.selectedOptions?.[0];

    const optionName =
      selectedOption?.dataset.optionName ||
      (select.name?.match(/^options\[(.*)\]$/)?.[1] || '');

    if (fenisaIsColorOptionName(optionName)) {
      return select.value || selectedOption?.value || '';
    }
  }

  return '';
};

const fenisaReadColorFromControl = (control) => {
  if (!control) return '';

  const optionName =
    control.dataset?.optionName ||
    (control.name?.match(/^options\[(.*)\]$/)?.[1] || '');

  if (!fenisaIsColorOptionName(optionName)) return '';

  return control.value || control.selectedOptions?.[0]?.value || '';
};

const fenisaGetVGGroup = (element) => {
  const img = element.querySelector?.('img');

  if (!img) return '';

  if (img.dataset.fenisaVgGroup) {
    return img.dataset.fenisaVgGroup;
  }

  const alt = img.getAttribute('alt') || '';

  const match = alt.match(/^\s*\[vg:([^\]]+)\]\s*/i);

  if (!match) return '';

  img.dataset.fenisaVgGroup = match[1].trim();

  img.setAttribute(
    'alt',
    alt.replace(match[0], '').trim()
  );

  return img.dataset.fenisaVgGroup;
};

const fenisaShouldShowVG = (group, selectedColor) => {
  if (!group) return true;

  const normalizedGroup = fenisaNormalizeVG(group);

  if (
    normalizedGroup === 'all' ||
    normalizedGroup === 'tümü'
  ) {
    return true;
  }

  if (!selectedColor) return true;

  return normalizedGroup === fenisaNormalizeVG(selectedColor);
};

const fenisaFilterVGElements = (
  root,
  selector,
  selectedColor
) => {
  root.querySelectorAll(selector).forEach((element) => {
    const group = fenisaGetVGGroup(element);

    const visible = fenisaShouldShowVG(
      group,
      selectedColor
    );

    if (visible) {
      element.removeAttribute(
        'data-fenisa-vg-hidden'
      );

      element.removeAttribute(
        'aria-hidden'
      );
    } else {
      element.setAttribute(
        'data-fenisa-vg-hidden',
        'true'
      );

      element.setAttribute(
        'aria-hidden',
        'true'
      );
    }
  });
};

const fenisaActivateFirstVisibleVG = (root) => {
  const activeSlide = root.querySelector(
    'slideshow-slide[aria-hidden="false"]'
  );

  if (
    activeSlide &&
    activeSlide.getAttribute(
      'data-fenisa-vg-hidden'
    ) !== 'true'
  ) {
    return;
  }

  const visibleThumbnail = Array.from(
    root.querySelectorAll(
      'slideshow-controls .slideshow-controls__thumbnail, slideshow-controls button'
    )
  ).find(
    (button) =>
      button.getAttribute(
        'data-fenisa-vg-hidden'
      ) !== 'true' &&
      button.querySelector('img')
  );

  visibleThumbnail?.click();
};

const fenisaApplyQuickAddVG = (
  forcedColor = ''
) => {
  const modalContent =
    document.getElementById(
      'quick-add-modal-content'
    );

  if (!modalContent) return;

  const selectedColor =
    forcedColor ||
    fenisaGetSelectedColor(modalContent);

  fenisaFilterVGElements(
    modalContent,
    'slideshow-slide',
    selectedColor
  );

  fenisaFilterVGElements(
    modalContent,
    'slideshow-controls .slideshow-controls__thumbnail, slideshow-controls button',
    selectedColor
  );

  fenisaFilterVGElements(
    modalContent,
    '.media-gallery__grid > li',
    selectedColor
  );

  fenisaFilterVGElements(
    modalContent,
    '.dialog-thumbnails-list__thumbnail',
    selectedColor
  );

  fenisaFilterVGElements(
    modalContent,
    '.dialog-zoomed-gallery > li',
    selectedColor
  );

  requestAnimationFrame(() => {
    fenisaActivateFirstVisibleVG(
      modalContent
    );
  });
};

const fenisaInstallQuickAddVG = () => {
  const modalContent =
    document.getElementById(
      'quick-add-modal-content'
    );

  if (!modalContent) return;

  if (
    modalContent.dataset
      .fenisaVgInstalled === 'true'
  ) {
    fenisaApplyQuickAddVG();
    return;
  }

  modalContent.dataset
    .fenisaVgInstalled = 'true';

  let selectedColorHint = '';
  let frame = 0;

  const schedule = (color = '') => {
    if (color) {
      selectedColorHint = color;
    }

    cancelAnimationFrame(frame);

    frame = requestAnimationFrame(() => {
      fenisaApplyQuickAddVG(
        selectedColorHint ||
          fenisaGetSelectedColor(
            modalContent
          )
      );
    });
  };

  modalContent.addEventListener(
    'change',
    (event) => {
      if (
        !event.target.closest(
          'variant-picker'
        )
      ) {
        return;
      }

      const color =
        fenisaReadColorFromControl(
          event.target
        );

      schedule(color);
    },
    true
  );

  modalContent.addEventListener(
    'input',
    (event) => {
      if (
        !event.target.closest(
          'variant-picker'
        )
      ) {
        return;
      }

      const color =
        fenisaReadColorFromControl(
          event.target
        );

      schedule(color);
    },
    true
  );

  modalContent.addEventListener(
    'click',
    (event) => {
      const picker =
        event.target.closest(
          'variant-picker'
        );

      if (!picker) return;

      const label =
        event.target.closest(
          'label[for]'
        );

      if (!label) return;

      const input =
        picker.querySelector(
          `#${CSS.escape(
            label.htmlFor
          )}`
        );

      const color =
        fenisaReadColorFromControl(
          input
        );

      if (!color) return;

      selectedColorHint = color;

      fenisaApplyQuickAddVG(
        color
      );
    },
    true
  );

  const observer =
    new MutationObserver(
      (mutations) => {
        let relevantChange = false;

        for (const mutation of mutations) {
          for (
            const node of
            mutation.addedNodes
          ) {
            if (
              !(node instanceof Element)
            ) {
              continue;
            }

            if (
              node.matches?.(
                'media-gallery, slideshow-slide, slideshow-controls, .media-gallery__grid, img'
              ) ||
              node.querySelector?.(
                'media-gallery, slideshow-slide, slideshow-controls, .media-gallery__grid, img'
              )
            ) {
              relevantChange = true;
              break;
            }
          }

          if (relevantChange) break;
        }

        if (relevantChange) {
          schedule();
        }
      }
    );

  observer.observe(
    modalContent,
    {
      childList: true,
      subtree: true,
    }
  );

  schedule();
};

if (
  !document.getElementById(
    'fenisa-quick-add-vg-style'
  )
) {
  const style =
    document.createElement('style');

  style.id =
    'fenisa-quick-add-vg-style';

  style.textContent = `
    #quick-add-modal-content [data-fenisa-vg-hidden='true'] {
      display: none !important;
    }
  `;

  document.head.appendChild(style);
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {AbortController} */
  #cartUpdateAbortController =
    new AbortController();

  get productPageUrl() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );

    if (productCard) {
      return productCard.productPageUrl;
    }

    const hotspotProduct =
      /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
        this.closest(
          'product-hotspot-component'
        )
      );

    const productLink =
      hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  #getSelectedVariantId() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );

    return (
      productCard?.getSelectedVariantId() ??
      null
    );
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener(
      'change',
      this.#closeQuickAddModal
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
      {
        signal:
          this.#cartUpdateAbortController
            .signal,
      }
    );

    document.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener(
      'change',
      this.#closeQuickAddModal
    );

    this.#abortController?.abort();

    this.#cartUpdateAbortController.abort();

    document.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  #handleProductSelectUpdate =
    (event) => {
      if (
        !(
          event.target instanceof
          HTMLElement
        )
      ) {
        return;
      }

      if (
        event.target.closest(
          'product-card'
        ) !==
        this.closest('product-card')
      ) {
        return;
      }

      if (
        this.dataset.usesSellingPlans ===
        'true'
      ) {
        return;
      }

      if (
        this.dataset.rendersBothButtons !==
        'true'
      ) {
        return;
      }

      const productOptionsCount =
        this.dataset.productOptionsCount;

      const quickAddButton =
        productOptionsCount === '1'
          ? 'add'
          : 'choose';

      this.setAttribute(
        'data-quick-add-button',
        quickAddButton
      );
    };

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  #updateVariantPicker(newHtml) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!modalContent) return;

    const variantPicker =
      /** @type {VariantPicker | null} */ (
        modalContent.querySelector(
          'variant-picker'
        )
      );

    if (!variantPicker) return;

    variantPicker.updateVariantPicker(
      newHtml
    );
  }

  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl =
      this.productPageUrl;

    if (
      this.dataset.usesSellingPlans ===
      'true'
    ) {
      if (currentUrl) {
        window.location.href =
          currentUrl;
      }

      return;
    }

    let productGrid =
      this.#cachedContent.get(
        currentUrl
      );

    if (!productGrid) {
      const html =
        await this.fetchProductPage(
          currentUrl
        );

      if (html) {
        const gridElement =
          html.querySelector(
            '[data-product-grid-content]'
          );

        if (gridElement) {
          productGrid =
            /** @type {Element} */ (
              gridElement.cloneNode(true)
            );

          this.#cachedContent.set(
            currentUrl,
            productGrid
          );
        }
      }
    }

    if (productGrid) {
      const freshContent =
        /** @type {Element} */ (
          productGrid.cloneNode(true)
        );

      await this.updateQuickAddModal(
        freshContent
      );

      this.#updateVariantPicker(
        productGrid
      );
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(
        dialogComponent instanceof
        QuickAddDialog
      )
    ) {
      return;
    }

    const productDetails =
      dialogComponent.querySelector(
        '.product-details'
      );

    const productMedia =
      dialogComponent.querySelector(
        '.product-information__media'
      );

    productDetails?.scrollTo({
      top: 0,
      behavior: 'instant',
    });

    productMedia?.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }

  #stayVisibleUntilDialogCloses(
    dialogComponent
  ) {
    this.toggleAttribute(
      'stay-visible',
      true
    );

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute(
          'stay-visible',
          false
        );
      },
      {
        once: true,
      }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(
        dialogComponent instanceof
        QuickAddDialog
      )
    ) {
      return;
    }

    this.#stayVisibleUntilDialogCloses(
      dialogComponent
    );

    dialogComponent.showDialog();

    fenisaInstallQuickAddVG();
    fenisaApplyQuickAddVG();

    const dialog =
      dialogComponent.refs?.dialog;

    if (!dialog) return;

    dialog.addEventListener(
      'animationstart',
      this.#resetScroll.bind(this),
      {
        once: true,
      }
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(
        dialogComponent instanceof
        QuickAddDialog
      )
    ) {
      return;
    }

    dialogComponent.closeDialog();
  };

  async fetchProductPage(
    productPageUrl
  ) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();

    this.#abortController =
      new AbortController();

    try {
      const response = await fetch(
        productPageUrl,
        {
          signal:
            this.#abortController.signal,
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        );
      }

      const responseText =
        await response.text();

      const html =
        new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

      return html;
    } catch (error) {
      if (
        error.name === 'AbortError'
      ) {
        return null;
      }

      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  async updateQuickAddModal(
    productGrid
  ) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (
      !productGrid ||
      !modalContent
    ) {
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails =
        productGrid.querySelector(
          '.product-details'
        );

      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );

      const variantPicker =
        productGrid.querySelector(
          'variant-picker'
        );

      const productPrice =
        productGrid.querySelector(
          'product-price'
        );

      const productTitle =
        document.createElement('a');

      productTitle.textContent =
        this.dataset.productTitle || '';

      productTitle.href =
        this.productPageUrl;

      const productHeader =
        document.createElement('div');

      productHeader.classList.add(
        'product-header'
      );

      productHeader.appendChild(
        productTitle
      );

      if (productPrice) {
        productHeader.appendChild(
          productPrice
        );
      }

      productGrid.appendChild(
        productHeader
      );

      if (variantPicker) {
        productGrid.appendChild(
          variantPicker
        );
      }

      if (productFormComponent) {
        productGrid.appendChild(
          productFormComponent
        );
      }

      productDetails?.remove();
    }

    const payload =
      productGrid.getAttribute(
        'view-event-payload'
      ) || '';

    modalContent.setAttribute(
      'view-event-payload',
      payload
    );

    morph(
      modalContent,
      productGrid
    );

    this.#syncVariantSelection(
      modalContent
    );

    fenisaInstallQuickAddVG();
    fenisaApplyQuickAddVG();
  }

  #syncVariantSelection(
    modalContent
  ) {
    const selectedVariantId =
      this.#getSelectedVariantId();

    if (!selectedVariantId) {
      return;
    }

    const modalInputs =
      modalContent.querySelectorAll(
        'input[type="radio"][data-variant-id]'
      );

    for (const input of modalInputs) {
      if (
        input instanceof
          HTMLInputElement &&
        input.dataset.variantId ===
          selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;

        input.dispatchEvent(
          new Event(
            'change',
            {
              bubbles: true,
            }
          )
        );

        break;
      }
    }
  }
}

if (
  !customElements.get(
    'quick-add-component'
  )
) {
  customElements.define(
    'quick-add-component',
    QuickAddComponent
  );
}

class QuickAddDialog extends DialogComponent {
  #abortController =
    new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.handleCartUpdate,
      {
        signal:
          this.#abortController.signal,
      }
    );

    this.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        this.closeDialog();
      })
      .catch((error) => {
        if (
          error?.name !==
          'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  #handleProductSelect = (
    event
  ) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        const anchorElement =
          /** @type {HTMLAnchorElement} */ (
            html.querySelector(
              '.view-product-title a'
            )
          );

        const viewMoreDetailsLink =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.view-product-title a'
            )
          );

        const mobileProductTitle =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.product-header a'
            )
          );

        if (!anchorElement) return;

        if (
          viewMoreDetailsLink
        ) {
          viewMoreDetailsLink.href =
            anchorElement.href;
        }

        if (
          mobileProductTitle
        ) {
          mobileProductTitle.href =
            anchorElement.href;
        }

        requestAnimationFrame(
          () => {
            fenisaInstallQuickAddVG();
            fenisaApplyQuickAddVG();
          }
        );
      })
      .catch((error) => {
        if (
          error?.name !==
          'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  #handleDialogClose = () => {
    const iosVersion =
      getIOSVersion();

    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (
        iosVersion.major === 16 &&
        iosVersion.minor >= 4
      )
    ) {
      return;
    }

    requestAnimationFrame(() => {
      const grid =
        document.querySelector(
          '#ResultsList [product-grid-view]'
        );

      if (grid) {
        const currentWidth =
          grid.getBoundingClientRect()
            .width;

        grid.style.width =
          `${currentWidth - 1}px`;

        requestAnimationFrame(
          () => {
            grid.style.width = '';
          }
        );
      }
    });
  };
}

if (
  !customElements.get(
    'quick-add-dialog'
  )
) {
  customElements.define(
    'quick-add-dialog',
    QuickAddDialog
  );
}